'use client'

import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import type { WorkspaceSubscription } from '@/lib/types'
import { CommonResp } from '@/backend/type'
import { Loader2Icon } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

interface IIncompleteSubscriptionBannerProps {
  subscription: WorkspaceSubscription
}

/**
 * Banner component to display when user has incomplete subscription
 * Provides actions to complete payment or remove the subscription
 * @param subscription - The incomplete workspace subscription
 * @returns Banner with action buttons for incomplete subscriptions
 */
export function IncompleteSubscriptionBanner({
  subscription,
}: IIncompleteSubscriptionBannerProps) {
  const t = useTranslations('pricing-v3')
  const [isCompletingPayment, setIsCompletingPayment] = useState(false)
  const [isCancelingIncomplete, setIsCancelingIncomplete] = useState(false)

  const isExpired = subscription.status === 'incomplete_expired'

  /**
   * Handle completing payment for incomplete subscription
   */
  const handleCompletePayment = async () => {
    if (isCompletingPayment) return

    setIsCompletingPayment(true)
    try {
      const response = await fetch('/api/pricing-v3/complete-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId: subscription.workspace_id,
          subscriptionId: subscription.subscription_id,
        }),
      })

      const respJson: CommonResp = await response.json()

      if (respJson.success && respJson.data?.paymentUrl) {
        window.open(respJson.data.paymentUrl, '_blank')
        toast.success(t('incomplete_banner.redirecting_to_payment'))
      } else {
        throw new Error(
          respJson.message || t('incomplete_banner.failed_payment_url'),
        )
      }
    } catch (error) {
      console.error('Complete payment error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : t('incomplete_banner.failed_complete_payment'),
      )
    } finally {
      setIsCompletingPayment(false)
    }
  }

  /**
   * Handle removing incomplete/expired subscription
   */
  const handleCancelIncomplete = async () => {
    if (isCancelingIncomplete) return

    setIsCancelingIncomplete(true)
    try {
      const response = await fetch('/api/pricing-v3/cancel-incomplete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId: subscription.workspace_id,
          subscriptionId: subscription.subscription_id,
        }),
      })

      const respJson: CommonResp = await response.json()

      if (respJson.success) {
        toast.success(t('incomplete_banner.subscription_removed'))
        // Refresh the page after a short delay
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        throw new Error(
          respJson.message || t('incomplete_banner.failed_remove_subscription'),
        )
      }
    } catch (error) {
      console.error('Cancel incomplete subscription error:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : t('incomplete_banner.failed_remove_subscription'),
      )
    } finally {
      setIsCancelingIncomplete(false)
    }
  }

  return (
    <div className="w-full max-w-screen-xl mx-auto my-6 bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 rounded-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
            <div className="flex flex-col gap-0.5">
              <h3 className="text-sm font-semibold text-white">
                {isExpired
                  ? t('incomplete_banner.expired_title')
                  : t('incomplete_banner.incomplete_title')}
              </h3>
              <p className="text-xs text-white/70">
                {isExpired
                  ? t('incomplete_banner.expired_description')
                  : t('incomplete_banner.incomplete_description')}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap">
            {!isExpired && (
              <Button
                variant="default"
                size="sm"
                className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                onClick={handleCompletePayment}
                disabled={isCompletingPayment || isCancelingIncomplete}
              >
                {isCompletingPayment ? (
                  <>
                    <Loader2Icon className="mr-1 h-3 w-3 animate-spin" />
                    {t('incomplete_banner.processing')}
                  </>
                ) : (
                  t('incomplete_banner.complete_payment')
                )}
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              className="h-8 text-xs"
              onClick={handleCancelIncomplete}
              disabled={isCompletingPayment || isCancelingIncomplete}
            >
              {isCancelingIncomplete ? (
                <>
                  <Loader2Icon className="mr-1 h-3 w-3 animate-spin" />
                  {t('incomplete_banner.removing')}
                </>
              ) : isExpired ? (
                t('incomplete_banner.remove_expired')
              ) : (
                t('incomplete_banner.cancel_subscription')
              )}
            </Button>
            <Link href="/subscription">
              <Button variant="active" size="sm" className="h-8 text-xs">
                {t('incomplete_banner.go_to_subscriptions')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
